import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOffIcon from "@mui/icons-material/FolderOff";
import HubIcon from "@mui/icons-material/Hub";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import EventsFormDialog from "./EventsFormDialog";
import { useAdminReportsApi } from "../hooks/useAdminReportsApi";
import type {
  AdminBacklogRow,
  DevCommentBacklogRow,
  DuplicateContractNameLatestHeadsResponse,
  DuplicateEventCodeVersionResponse,
  DuplicateLegacyEventCodeResponse,
  EmptyContractUserEventRow,
  PairGapRow,
} from "../shared/adminReports";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";

const FLAT_ACCORDION_SX = {
  border: "none",
  boxShadow: "none",
  "&::before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

const commentCellEllipsis: React.CSSProperties = {
  maxWidth: 320,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const hmiCellEllipsis: React.CSSProperties = {
  maxWidth: 280,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

type AdminApprovalToolsProps = {
  /** When true, omit outer hint and tighten spacing (used inside Admin page panel). */
  embedded?: boolean;
  initialDevCommentRows?: DevCommentBacklogRow[] | null;
  initialBacklogRows?: AdminBacklogRow[] | null;
  initialDuplicateReport?: DuplicateEventCodeVersionResponse | null;
  initialDuplicateLegacyReport?: DuplicateLegacyEventCodeResponse | null;
  initialDuplicateContractNameReport?: DuplicateContractNameLatestHeadsResponse | null;
  /** Prefetched total from Admin header metrics so the summary chip shows before first expand (optional). */
  initialBacklogTotal?: number;
  initialDevCommentTotal?: number;
  initialEmptyContractTotal?: number;
  initialEmptyContractRows?: EmptyContractUserEventRow[] | null;
  initialIntegrityData?: {
    missing_resolving: PairGapRow[];
    missing_rising: PairGapRow[];
  } | null;
};

function BacklogTable({
  rows,
  onOpenEvent,
}: {
  rows: AdminBacklogRow[];
  onOpenEvent: (id: number) => void;
}) {
  return (
    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Event Code</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Ver.</TableCell>
          <TableCell>Created</TableCell>
          <TableCell>HMI text (ENG)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.global_event_id} hover>
            <TableCell>
              <Button
                size="small"
                variant="text"
                onClick={() => onOpenEvent(r.global_event_id)}
                sx={{ textTransform: "none", minWidth: 0, p: 0 }}
              >
                {r.global_event_id}
              </Button>
            </TableCell>
            <TableCell>{r.event_code}</TableCell>
            <TableCell>{r.event_type}</TableCell>
            <TableCell>{r.version ?? "—"}</TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {formatLocalDateTime(r.created_at)}
            </TableCell>
            <TableCell sx={hmiCellEllipsis} title={r.english_hmi_label ?? ""}>
              {r.english_hmi_label?.trim() ? r.english_hmi_label : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DevCommentBacklogTable({
  rows,
  onOpenEvent,
}: {
  rows: DevCommentBacklogRow[];
  onOpenEvent: (id: number) => void;
}) {
  return (
    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Event Code</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Ver.</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Created</TableCell>
          <TableCell>Dev comment</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.global_event_id} hover>
            <TableCell>
              <Button
                size="small"
                variant="text"
                onClick={() => onOpenEvent(r.global_event_id)}
                sx={{ textTransform: "none", minWidth: 0, p: 0 }}
              >
                {r.global_event_id}
              </Button>
            </TableCell>
            <TableCell>{r.event_code}</TableCell>
            <TableCell>{r.event_type}</TableCell>
            <TableCell>{r.version ?? "—"}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={r.approved_at ? "Approved" : "Unapproved"}
                color={r.approved_at ? "success" : "warning"}
                variant="outlined"
              />
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {formatLocalDateTime(r.created_at)}
            </TableCell>
            <TableCell sx={commentCellEllipsis} title={r.dev_comment}>
              {r.dev_comment}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function EmptyContractUserEventsTable({
  rows,
  onOpenEvent,
}: {
  rows: EmptyContractUserEventRow[];
  onOpenEvent: (id: number) => void;
}) {
  return (
    <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Event Code</TableCell>
          <TableCell>Ver.</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Created</TableCell>
          <TableCell>HMI text (ENG)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.global_event_id} hover>
            <TableCell>
              <Button
                size="small"
                variant="text"
                onClick={() => onOpenEvent(r.global_event_id)}
                sx={{ textTransform: "none", minWidth: 0, p: 0 }}
              >
                {r.global_event_id}
              </Button>
            </TableCell>
            <TableCell>{r.event_code}</TableCell>
            <TableCell>{r.version ?? "—"}</TableCell>
            <TableCell>
              <Chip
                size="small"
                label={r.approved_at ? "Approved" : "Unapproved"}
                color={r.approved_at ? "success" : "warning"}
                variant="outlined"
              />
            </TableCell>
            <TableCell sx={{ whiteSpace: "nowrap" }}>
              {formatLocalDateTime(r.created_at)}
            </TableCell>
            <TableCell sx={hmiCellEllipsis} title={r.english_hmi_label ?? ""}>
              {r.english_hmi_label?.trim() ? r.english_hmi_label : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function GapTable({
  rows,
  onOpenEvent,
}: {
  rows: PairGapRow[];
  onOpenEvent: (id: number) => void;
}) {
  return (
    <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
      <TableHead>
        <TableRow>
          <TableCell>ID</TableCell>
          <TableCell>Event Code</TableCell>
          <TableCell>Expected Event Code</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Ver.</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={`${r.gap}-${r.global_event_id}-${r.event_code}`} hover>
            <TableCell>
              <Button
                size="small"
                variant="text"
                onClick={() => onOpenEvent(r.global_event_id)}
                sx={{ textTransform: "none", minWidth: 0, p: 0 }}
              >
                {r.global_event_id}
              </Button>
            </TableCell>
            <TableCell>{r.event_code}</TableCell>
            <TableCell>{r.expected_counterpart_code}</TableCell>
            <TableCell>{r.event_type}</TableCell>
            <TableCell>{r.version ?? "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const DEFAULT_ROWS_PER_PAGE = 10;

const AdminApprovalTools: React.FC<AdminApprovalToolsProps> = ({
  embedded = false,
  initialDevCommentRows = null,
  initialBacklogRows = null,
  initialBacklogTotal,
  initialDuplicateReport = null,
  initialDuplicateLegacyReport = null,
  initialDuplicateContractNameReport = null,
  initialEmptyContractTotal,
  initialEmptyContractRows = null,
  initialIntegrityData = null,
  initialDevCommentTotal,
}) => {
  const {
    fetchUnapprovedBacklog,
    fetchDevCommentBacklog,
    fetchDuplicateUserEventCodeVersion,
    fetchDuplicateLegacyEventCode,
    fetchDuplicateContractNamesLatestHeads,
    fetchUserEventsEmptyContractLatest,
    fetchRisingResolvingIntegrity,
  } = useAdminReportsApi();

  const [emptyContractOpen, setEmptyContractOpen] = useState(false);
  const [emptyContractLoading, setEmptyContractLoading] = useState(false);
  const [emptyContractError, setEmptyContractError] = useState<string | null>(null);
  const [emptyContractRows, setEmptyContractRows] = useState<EmptyContractUserEventRow[] | null>(
    initialEmptyContractRows
  );
  const [emptyContractTotal, setEmptyContractTotal] = useState(0);
  const [emptyContractServerPage, setEmptyContractServerPage] = useState(0);
  const [emptyContractRowsPerPage, setEmptyContractRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [eventCodeFilterInput, setEventCodeFilterInput] = useState("");
  const [eventCodeFilterApplied, setEventCodeFilterApplied] = useState("");

  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [devCommentOpen, setDevCommentOpen] = useState(false);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [integrityOpen, setIntegrityOpen] = useState(false);

  const [devCommentLoading, setDevCommentLoading] = useState(false);
  const [devCommentError, setDevCommentError] = useState<string | null>(null);
  const [devCommentRows, setDevCommentRows] = useState<DevCommentBacklogRow[] | null>(
    initialDevCommentRows
  );
  const [devCommentTotal, setDevCommentTotal] = useState(initialDevCommentTotal ?? 0);
  const [devCommentPage, setDevCommentPage] = useState(0);
  const [devCommentRowsPerPage, setDevCommentRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [backlogLoading, setBacklogLoading] = useState(false);
  const [backlogError, setBacklogError] = useState<string | null>(null);
  const [backlogRows, setBacklogRows] = useState<AdminBacklogRow[] | null>(initialBacklogRows);
  const [backlogTotal, setBacklogTotal] = useState(initialBacklogTotal ?? 0);
  const [backlogPage, setBacklogPage] = useState(0);
  const [backlogRowsPerPage, setBacklogRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [duplicateData, setDuplicateData] = useState<DuplicateEventCodeVersionResponse | null>(
    initialDuplicateReport ?? null
  );
  const [dupGroupPage, setDupGroupPage] = useState(0);
  const [dupGroupsPerPage, setDupGroupsPerPage] = useState(5);

  const [duplicateLegacyOpen, setDuplicateLegacyOpen] = useState(false);
  const [duplicateLegacyLoading, setDuplicateLegacyLoading] = useState(false);
  const [duplicateLegacyError, setDuplicateLegacyError] = useState<string | null>(null);
  const [duplicateLegacyData, setDuplicateLegacyData] =
    useState<DuplicateLegacyEventCodeResponse | null>(initialDuplicateLegacyReport ?? null);
  const [dupLegacyGroupPage, setDupLegacyGroupPage] = useState(0);
  const [dupLegacyGroupsPerPage, setDupLegacyGroupsPerPage] = useState(5);

  const [dupContractOpen, setDupContractOpen] = useState(false);
  const [dupContractLoading, setDupContractLoading] = useState(false);
  const [dupContractError, setDupContractError] = useState<string | null>(null);
  const [dupContractData, setDupContractData] =
    useState<DuplicateContractNameLatestHeadsResponse | null>(initialDuplicateContractNameReport ?? null);
  const [dupContractGroupPage, setDupContractGroupPage] = useState(0);
  const [dupContractGroupsPerPage, setDupContractGroupsPerPage] = useState(5);

  const [intLoading, setIntLoading] = useState(false);
  const [intError, setIntError] = useState<string | null>(null);
  const [intData, setIntData] = useState<{
    missing_resolving: PairGapRow[];
    missing_rising: PairGapRow[];
  } | null>(initialIntegrityData);
  const [missResPage, setMissResPage] = useState(0);
  const [missResRowsPerPage, setMissResRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [missRisPage, setMissRisPage] = useState(0);
  const [missRisRowsPerPage, setMissRisRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [editEventId, setEditEventId] = useState<number | null>(null);

  React.useEffect(() => {
    if (initialDevCommentRows !== null) {
      setDevCommentRows(initialDevCommentRows);
      setDevCommentPage(0);
    }
  }, [initialDevCommentRows]);

  React.useEffect(() => {
    if (initialDevCommentTotal !== undefined) {
      setDevCommentTotal(initialDevCommentTotal);
    }
  }, [initialDevCommentTotal]);

  React.useEffect(() => {
    if (initialBacklogRows !== null) {
      setBacklogRows(initialBacklogRows);
      setBacklogPage(0);
    }
  }, [initialBacklogRows]);

  React.useEffect(() => {
    if (initialBacklogTotal !== undefined) {
      setBacklogTotal(initialBacklogTotal);
    }
  }, [initialBacklogTotal]);

  React.useEffect(() => {
    if (initialIntegrityData !== null) {
      setIntData(initialIntegrityData);
      setMissResPage(0);
      setMissRisPage(0);
    }
  }, [initialIntegrityData]);

  React.useEffect(() => {
    if (initialDuplicateReport !== null) {
      setDuplicateData(initialDuplicateReport);
      setDupGroupPage(0);
    }
  }, [initialDuplicateReport]);

  React.useEffect(() => {
    if (initialDuplicateLegacyReport !== null) {
      setDuplicateLegacyData(initialDuplicateLegacyReport);
      setDupLegacyGroupPage(0);
    }
  }, [initialDuplicateLegacyReport]);

  React.useEffect(() => {
    if (initialDuplicateContractNameReport !== null) {
      setDupContractData(initialDuplicateContractNameReport);
      setDupContractGroupPage(0);
    }
  }, [initialDuplicateContractNameReport]);

  const loadEmptyContractUserEvents = useCallback(
    async (page: number, pageSize: number, appliedEventCode: string) => {
      setEmptyContractLoading(true);
      setEmptyContractError(null);
      try {
        const trim = appliedEventCode.trim();
        const parsed =
          trim !== "" && !Number.isNaN(Number(trim)) ? Number(trim) : undefined;
        const res = await fetchUserEventsEmptyContractLatest({
          limit: pageSize,
          offset: page * pageSize,
          ...(parsed !== undefined ? { event_code: parsed } : {}),
        });
        setEmptyContractRows(res?.results ?? []);
        setEmptyContractTotal(res?.total ?? 0);
        setEmptyContractServerPage(page);
        setEmptyContractRowsPerPage(pageSize);
      } catch (e) {
        const err = e as Error & { responseBody?: string };
        setEmptyContractError(
          err.responseBody || err.message || "Could not load empty contract name list."
        );
      } finally {
        setEmptyContractLoading(false);
      }
    },
    [fetchUserEventsEmptyContractLatest]
  );

  const loadDuplicateReport = useCallback(async () => {
    setDuplicateLoading(true);
    setDuplicateError(null);
    try {
      const res = await fetchDuplicateUserEventCodeVersion({ limit: 100 });
      setDuplicateData(res ?? null);
      setDupGroupPage(0);
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setDuplicateError(
        err.responseBody || err.message || "Could not load duplicate code/version report."
      );
    } finally {
      setDuplicateLoading(false);
    }
  }, [fetchDuplicateUserEventCodeVersion]);

  const loadDuplicateLegacyReport = useCallback(async () => {
    setDuplicateLegacyLoading(true);
    setDuplicateLegacyError(null);
    try {
      const res = await fetchDuplicateLegacyEventCode({ limit: 100 });
      setDuplicateLegacyData(res ?? null);
      setDupLegacyGroupPage(0);
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setDuplicateLegacyError(
        err.responseBody || err.message || "Could not load duplicate legacy-code report."
      );
    } finally {
      setDuplicateLegacyLoading(false);
    }
  }, [fetchDuplicateLegacyEventCode]);

  const loadDuplicateContractNameReport = useCallback(async () => {
    setDupContractLoading(true);
    setDupContractError(null);
    try {
      const res = await fetchDuplicateContractNamesLatestHeads({ limit: 100 });
      setDupContractData(res ?? null);
      setDupContractGroupPage(0);
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setDupContractError(
        err.responseBody || err.message || "Could not load duplicate contract name report."
      );
    } finally {
      setDupContractLoading(false);
    }
  }, [fetchDuplicateContractNamesLatestHeads]);

  const loadDevCommentBacklog = useCallback(async () => {
    setDevCommentLoading(true);
    setDevCommentError(null);
    try {
      const res = await fetchDevCommentBacklog({ limit: 100 });
      setDevCommentRows(res?.results ?? []);
      setDevCommentTotal(res?.total ?? res?.count ?? res?.results?.length ?? 0);
      setDevCommentPage(0);
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setDevCommentError(
        err.responseBody || err.message || "Could not load dev comment backlog."
      );
    } finally {
      setDevCommentLoading(false);
    }
  }, [fetchDevCommentBacklog]);

  const loadBacklog = useCallback(async () => {
    setBacklogLoading(true);
    setBacklogError(null);
    try {
      const res = await fetchUnapprovedBacklog({ limit: 100 });
      setBacklogRows(res?.results ?? []);
      setBacklogTotal(res?.total ?? res?.count ?? res?.results?.length ?? 0);
      setBacklogPage(0);
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setBacklogError(
        err.responseBody || err.message || "Could not load approval backlog."
      );
    } finally {
      setBacklogLoading(false);
    }
  }, [fetchUnapprovedBacklog]);

  const loadIntegrity = useCallback(async () => {
    setIntLoading(true);
    setIntError(null);
    try {
      const res = await fetchRisingResolvingIntegrity();
      if (res) {
        setIntData(res);
        setMissResPage(0);
        setMissRisPage(0);
      }
    } catch (e) {
      const err = e as Error & { responseBody?: string };
      setIntError(
        err.responseBody || err.message || "Could not load pair integrity report."
      );
    } finally {
      setIntLoading(false);
    }
  }, [fetchRisingResolvingIntegrity]);

  const refreshAfterDialog = useCallback(() => {
    if (duplicateData !== null) void loadDuplicateReport();
    if (duplicateLegacyData !== null) void loadDuplicateLegacyReport();
    void loadDuplicateContractNameReport();
    if (devCommentRows !== null) void loadDevCommentBacklog();
    if (backlogRows !== null) void loadBacklog();
    if (intData !== null) void loadIntegrity();
    if (emptyContractRows !== null) {
      void loadEmptyContractUserEvents(
        emptyContractServerPage,
        emptyContractRowsPerPage,
        eventCodeFilterApplied
      );
    }
  }, [
    duplicateData,
    duplicateLegacyData,
    devCommentRows,
    backlogRows,
    intData,
    emptyContractRows,
    emptyContractServerPage,
    emptyContractRowsPerPage,
    eventCodeFilterApplied,
    loadDuplicateReport,
    loadDuplicateLegacyReport,
    loadDuplicateContractNameReport,
    loadDevCommentBacklog,
    loadBacklog,
    loadIntegrity,
    loadEmptyContractUserEvents,
  ]);

  const devSlice =
    devCommentRows?.slice(
      devCommentPage * devCommentRowsPerPage,
      devCommentPage * devCommentRowsPerPage + devCommentRowsPerPage
    ) ?? [];

  const backlogSlice =
    backlogRows?.slice(
      backlogPage * backlogRowsPerPage,
      backlogPage * backlogRowsPerPage + backlogRowsPerPage
    ) ?? [];

  const missResRows = intData?.missing_resolving ?? [];
  const missRisRows = intData?.missing_rising ?? [];
  const missResSlice = missResRows.slice(
    missResPage * missResRowsPerPage,
    missResPage * missResRowsPerPage + missResRowsPerPage
  );
  const missRisSlice = missRisRows.slice(
    missRisPage * missRisRowsPerPage,
    missRisPage * missRisRowsPerPage + missRisRowsPerPage
  );

  const duplicateGroups = duplicateData?.groups ?? [];
  const dupSlice = duplicateGroups.slice(
    dupGroupPage * dupGroupsPerPage,
    dupGroupPage * dupGroupsPerPage + dupGroupsPerPage
  );

  const duplicateLegacyGroups = duplicateLegacyData?.groups ?? [];
  const dupLegacySlice = duplicateLegacyGroups.slice(
    dupLegacyGroupPage * dupLegacyGroupsPerPage,
    dupLegacyGroupPage * dupLegacyGroupsPerPage + dupLegacyGroupsPerPage
  );

  const dupContractGroups = dupContractData?.groups ?? [];
  const dupContractSlice = dupContractGroups.slice(
    dupContractGroupPage * dupContractGroupsPerPage,
    dupContractGroupPage * dupContractGroupsPerPage + dupContractGroupsPerPage
  );

  const emptyContractSummaryTotal =
    emptyContractRows !== null ? emptyContractTotal : initialEmptyContractTotal;
  const showEmptyContractSummaryChip =
    emptyContractRows !== null || initialEmptyContractTotal !== undefined;

  const backlogSummaryTotal = backlogRows !== null ? backlogTotal : initialBacklogTotal;
  const showBacklogSummaryChip = backlogRows !== null || initialBacklogTotal !== undefined;

  const devCommentSummaryTotal = devCommentRows !== null ? devCommentTotal : initialDevCommentTotal;
  const showDevCommentSummaryChip = devCommentRows !== null || initialDevCommentTotal !== undefined;

  return (
    <Stack spacing={1} sx={{ mt: embedded ? 0 : 2, mb: embedded ? 0 : 3 }}>
      {!embedded && (
        <Typography variant="body2" color="text.secondary">
          Data loads when you expand a section.
        </Typography>
      )}

      <Accordion
        expanded={backlogOpen}
        onChange={(_, expanded) => {
          setBacklogOpen(expanded);
          if (expanded && backlogRows === null && !backlogLoading) void loadBacklog();
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <PendingActionsIcon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Open approvals</Typography>
            {showBacklogSummaryChip && backlogSummaryTotal !== undefined && (
              <Chip
                size="small"
                color={backlogSummaryTotal > 0 ? "warning" : "success"}
                label={`${backlogSummaryTotal} total`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {backlogLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {backlogError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {backlogError}
            </Alert>
          )}
          {!backlogLoading && backlogRows && backlogRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No pending approvals (latest version per event key; non-manufacturer, not superseded).
            </Typography>
          )}
          {!backlogLoading && backlogRows && backlogRows.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                {backlogTotal > backlogRows.length
                  ? `Showing ${backlogRows.length} of ${backlogTotal} (API returns up to 100 rows, oldest first).`
                  : "Latest unapproved version per event key (non-manufacturer, not superseded). Paginated locally."}
              </Typography>
              <TableContainer sx={{ maxHeight: 380 }}>
                <BacklogTable rows={backlogSlice} onOpenEvent={setEditEventId} />
              </TableContainer>
              <TablePagination
                component="div"
                count={backlogRows.length}
                page={backlogPage}
                onPageChange={(_, p) => setBacklogPage(p)}
                rowsPerPage={backlogRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={(e) => {
                  setBacklogRowsPerPage(parseInt(e.target.value, 10));
                  setBacklogPage(0);
                }}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={devCommentOpen}
        onChange={(_, expanded) => {
          setDevCommentOpen(expanded);
          if (expanded && devCommentRows === null && !devCommentLoading) {
            void loadDevCommentBacklog();
          }
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <StickyNote2Icon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Open TODOs</Typography>
            {showDevCommentSummaryChip && devCommentSummaryTotal !== undefined && (
              <Chip
                size="small"
                color={devCommentSummaryTotal > 0 ? "warning" : "success"}
                label={`${devCommentSummaryTotal} total`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {devCommentLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {devCommentError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {devCommentError}
            </Alert>
          )}
          {!devCommentLoading && devCommentRows && devCommentRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No events with a dev comment on the latest version per code / type / contract.
            </Typography>
          )}
          {!devCommentLoading && devCommentRows && devCommentRows.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                {devCommentTotal > devCommentRows.length
                  ? `Showing ${devCommentRows.length} of ${devCommentTotal} (API returns up to 100 rows).`
                  : "Paginated locally (API returns up to 100 rows)."}
              </Typography>
              <TableContainer sx={{ maxHeight: 380 }}>
                <DevCommentBacklogTable rows={devSlice} onOpenEvent={setEditEventId} />
              </TableContainer>
              <TablePagination
                component="div"
                count={devCommentRows.length}
                page={devCommentPage}
                onPageChange={(_, p) => setDevCommentPage(p)}
                rowsPerPage={devCommentRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={(e) => {
                  setDevCommentRowsPerPage(parseInt(e.target.value, 10));
                  setDevCommentPage(0);
                }}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={duplicateOpen}
        onChange={(_, expanded) => {
          setDuplicateOpen(expanded);
          if (expanded && duplicateData === null && !duplicateLoading) void loadDuplicateReport();
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <ContentCopyIcon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Duplicate system event codes</Typography>
            {duplicateData !== null && (
              <Chip
                size="small"
                color={duplicateData.total_duplicate_groups > 0 ? "warning" : "success"}
                label={`${duplicateData.total_duplicate_groups} groups`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {duplicateLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {duplicateError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {duplicateError}
            </Alert>
          )}
          {!duplicateLoading && duplicateData && duplicateData.total_duplicate_groups === 0 && (
            <Typography variant="body2" color="text.secondary">
              No UserEvents (non-manufacturer) share the same event code and version in more than one row.
            </Typography>
          )}
          {!duplicateLoading && duplicateData && duplicateData.total_duplicate_groups > 0 && (
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary" display="block">
                Multiple rows with the same code and version usually differ by contract name. Review or merge as
                needed.
              </Typography>
              {duplicateData.total_duplicate_groups > duplicateData.groups.length && (
                <Typography variant="caption" color="warning.main" display="block">
                  Showing first {duplicateData.groups.length} of {duplicateData.total_duplicate_groups} groups (API
                  limit {duplicateData.limit}).
                </Typography>
              )}
              <Stack spacing={1.5}>
                {dupSlice.map((g) => (
                  <Paper key={`${g.event_code}-${String(g.version)}`} variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Event code {g.event_code} · version {g.version === null ? "—" : g.version} · {g.occurrences}{" "}
                      rows
                    </Typography>
                    <TableContainer>
                      <Table size="small" sx={{ minWidth: 520 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Contract name</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Approval</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.events.map((ev) => (
                            <TableRow key={ev.global_event_id} hover>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setEditEventId(ev.global_event_id)}
                                  sx={{ textTransform: "none", minWidth: 0, p: 0 }}
                                >
                                  {ev.global_event_id}
                                </Button>
                              </TableCell>
                              <TableCell sx={commentCellEllipsis} title={ev.event_contract_name ?? ""}>
                                {ev.event_contract_name?.trim() ? ev.event_contract_name : "—"}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {formatLocalDateTime(ev.created_at)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={ev.approved_at ? "Approved" : "Unapproved"}
                                  color={ev.approved_at ? "success" : "warning"}
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={duplicateGroups.length}
                page={dupGroupPage}
                onPageChange={(_, p) => setDupGroupPage(p)}
                rowsPerPage={dupGroupsPerPage}
                rowsPerPageOptions={[3, 5, 10, 25]}
                onRowsPerPageChange={(e) => {
                  setDupGroupsPerPage(parseInt(e.target.value, 10));
                  setDupGroupPage(0);
                }}
              />
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={duplicateLegacyOpen}
        onChange={(_, expanded) => {
          setDuplicateLegacyOpen(expanded);
          if (expanded && duplicateLegacyData === null && !duplicateLegacyLoading) {
            void loadDuplicateLegacyReport();
          }
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <ContentCopyIcon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Duplicate legacy event codes</Typography>
            {duplicateLegacyData !== null && (
              <Chip
                size="small"
                color={duplicateLegacyData.total_duplicate_groups > 0 ? "warning" : "success"}
                label={`${duplicateLegacyData.total_duplicate_groups} groups`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {duplicateLegacyLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {duplicateLegacyError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {duplicateLegacyError}
            </Alert>
          )}
          {!duplicateLegacyLoading &&
            duplicateLegacyData &&
            duplicateLegacyData.total_duplicate_groups === 0 && (
              <Typography variant="body2" color="text.secondary">
                No active non-manufacturer UserEvents share the same stored legacy event code.
              </Typography>
            )}
          {!duplicateLegacyLoading &&
            duplicateLegacyData &&
            duplicateLegacyData.total_duplicate_groups > 0 && (
              <Stack spacing={2}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Checks the latest row of each active event identity. Historical versions of the same event are
                  ignored. These collisions make legacy-code exports ambiguous.
                </Typography>
                {duplicateLegacyData.total_duplicate_groups > duplicateLegacyData.groups.length && (
                  <Typography variant="caption" color="warning.main" display="block">
                    Showing first {duplicateLegacyData.groups.length} of{" "}
                    {duplicateLegacyData.total_duplicate_groups} groups (API limit {duplicateLegacyData.limit}).
                  </Typography>
                )}
                <Stack spacing={1.5}>
                  {dupLegacySlice.map((group) => (
                    <Paper key={group.legacy_event_code} variant="outlined" sx={{ p: 1.25 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Legacy event code {group.legacy_event_code} · {group.occurrences} events
                      </Typography>
                      <TableContainer>
                        <Table size="small" sx={{ minWidth: 680 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>System event code</TableCell>
                              <TableCell>Contract name</TableCell>
                              <TableCell>Ver.</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Approval</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.events.map((event) => (
                              <TableRow key={event.global_event_id} hover>
                                <TableCell>
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setEditEventId(event.global_event_id)}
                                    sx={{ textTransform: "none", minWidth: 0, p: 0 }}
                                  >
                                    {event.global_event_id}
                                  </Button>
                                </TableCell>
                                <TableCell>{event.event_code}</TableCell>
                                <TableCell sx={commentCellEllipsis} title={event.event_contract_name ?? ""}>
                                  {event.event_contract_name?.trim() ? event.event_contract_name : "—"}
                                </TableCell>
                                <TableCell>{event.version ?? "—"}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {formatLocalDateTime(event.created_at)}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={event.approved_at ? "Approved" : "Unapproved"}
                                    color={event.approved_at ? "success" : "warning"}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  ))}
                </Stack>
                <TablePagination
                  component="div"
                  count={duplicateLegacyGroups.length}
                  page={dupLegacyGroupPage}
                  onPageChange={(_, page) => setDupLegacyGroupPage(page)}
                  rowsPerPage={dupLegacyGroupsPerPage}
                  rowsPerPageOptions={[3, 5, 10, 25]}
                  onRowsPerPageChange={(event) => {
                    setDupLegacyGroupsPerPage(parseInt(event.target.value, 10));
                    setDupLegacyGroupPage(0);
                  }}
                />
              </Stack>
            )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={dupContractOpen}
        onChange={(_, expanded) => {
          setDupContractOpen(expanded);
          if (expanded && !dupContractLoading) {
            void loadDuplicateContractNameReport();
          }
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <LabelOutlinedIcon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Duplicate contract names</Typography>
            {dupContractData !== null && (
              <Chip
                size="small"
                color={dupContractData.total_duplicate_contract_groups > 0 ? "warning" : "success"}
                label={`${dupContractData.total_duplicate_contract_groups} groups`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {dupContractLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {dupContractError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {dupContractError}
            </Alert>
          )}
          {!dupContractLoading && dupContractData && dupContractData.total_duplicate_contract_groups === 0 && (
            <Typography variant="body2" color="text.secondary">
              No duplicate normalized contract names among latest events (non-manufacturer UserEvents, not superseded;
              empty contracts excluded).
            </Typography>
          )}
          {!dupContractLoading && dupContractData && dupContractData.total_duplicate_contract_groups > 0 && (
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary" display="block">
                Same contract string after trim (and invisible character cleanup), compared case-insensitively. Only the
                latest row per module and event code is considered (highest version, tie-break by ID) — same scope as the
                empty-contract report.
              </Typography>
              {dupContractData.total_duplicate_contract_groups > dupContractData.groups.length && (
                <Typography variant="caption" color="warning.main" display="block">
                  Showing first {dupContractData.groups.length} of {dupContractData.total_duplicate_contract_groups}{" "}
                  groups (API limit {dupContractData.limit}).
                </Typography>
              )}
              <Stack spacing={1.5}>
                {dupContractSlice.map((g) => (
                  <Paper key={g.contract_key} variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Normalized key <code>{g.contract_key}</code> ·{" "}
                      {g.occurrences === 1 ? "1 latest event" : `${g.occurrences} latest events`}
                    </Typography>
                    <TableContainer>
                      <Table size="small" sx={{ minWidth: 680 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Event Code</TableCell>
                            <TableCell>Contract name</TableCell>
                            <TableCell>Ver.</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>HMI text (ENG)</TableCell>
                            <TableCell>Approval</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.events.map((ev) => (
                            <TableRow key={ev.global_event_id} hover>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setEditEventId(ev.global_event_id)}
                                  sx={{ textTransform: "none", minWidth: 0, p: 0 }}
                                >
                                  {ev.global_event_id}
                                </Button>
                              </TableCell>
                              <TableCell>{ev.event_code}</TableCell>
                              <TableCell sx={commentCellEllipsis} title={ev.event_contract_name ?? ""}>
                                {ev.event_contract_name?.trim() ? ev.event_contract_name : "—"}
                              </TableCell>
                              <TableCell>{ev.version ?? "—"}</TableCell>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                {formatLocalDateTime(ev.created_at)}
                              </TableCell>
                              <TableCell sx={hmiCellEllipsis} title={ev.english_hmi_label ?? ""}>
                                {ev.english_hmi_label?.trim() ? ev.english_hmi_label : "—"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={ev.approved_at ? "Approved" : "Unapproved"}
                                  color={ev.approved_at ? "success" : "warning"}
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))}
              </Stack>
              <TablePagination
                component="div"
                count={dupContractGroups.length}
                page={dupContractGroupPage}
                onPageChange={(_, p) => setDupContractGroupPage(p)}
                rowsPerPage={dupContractGroupsPerPage}
                rowsPerPageOptions={[3, 5, 10, 25]}
                onRowsPerPageChange={(e) => {
                  setDupContractGroupsPerPage(parseInt(e.target.value, 10));
                  setDupContractGroupPage(0);
                }}
              />
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={emptyContractOpen}
        onChange={(_, expanded) => {
          setEmptyContractOpen(expanded);
          if (expanded && emptyContractRows === null && !emptyContractLoading) {
            void loadEmptyContractUserEvents(0, emptyContractRowsPerPage, eventCodeFilterApplied);
          }
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <FolderOffIcon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Empty contract names</Typography>
            {showEmptyContractSummaryChip && emptyContractSummaryTotal !== undefined && (
              <Chip
                size="small"
                color={emptyContractSummaryTotal > 0 ? "warning" : "success"}
                label={`${emptyContractSummaryTotal} total`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {emptyContractLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {emptyContractError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {emptyContractError}
            </Alert>
          )}
          {!emptyContractLoading && emptyContractRows !== null && emptyContractTotal === 0 && (
            <Typography variant="body2" color="text.secondary">
              No matching UserEvents (empty or whitespace-only contract after trim; non-manufacturer,
              not reassignment-superseded).
            </Typography>
          )}
          {!emptyContractLoading && emptyContractRows !== null && emptyContractTotal > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                One row per event code (highest version; excludes manufacturer and superseded). Ordered by event
                code. Use the filter to jump to a code (e.g. 1001).
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  label="Filter by event code"
                  placeholder="e.g. 1001"
                  value={eventCodeFilterInput}
                  onChange={(e) => setEventCodeFilterInput(e.target.value)}
                  sx={{ minWidth: 160 }}
                  inputProps={{ inputMode: "numeric" }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const next = eventCodeFilterInput.trim();
                    setEventCodeFilterApplied(next);
                    void loadEmptyContractUserEvents(0, emptyContractRowsPerPage, next);
                  }}
                >
                  Apply
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEventCodeFilterInput("");
                    setEventCodeFilterApplied("");
                    void loadEmptyContractUserEvents(0, emptyContractRowsPerPage, "");
                  }}
                >
                  Clear
                </Button>
              </Stack>
              <TableContainer sx={{ maxHeight: 380 }}>
                <EmptyContractUserEventsTable rows={emptyContractRows} onOpenEvent={setEditEventId} />
              </TableContainer>
              <TablePagination
                component="div"
                count={emptyContractTotal}
                page={emptyContractServerPage}
                onPageChange={(_, p) => {
                  void loadEmptyContractUserEvents(p, emptyContractRowsPerPage, eventCodeFilterApplied);
                }}
                rowsPerPage={emptyContractRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  void loadEmptyContractUserEvents(0, n, eventCodeFilterApplied);
                }}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={integrityOpen}
        onChange={(_, expanded) => {
          setIntegrityOpen(expanded);
          if (expanded && intData === null && !intLoading) void loadIntegrity();
        }}
        disableGutters
        sx={FLAT_ACCORDION_SX}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
            <HubIcon color="primary" fontSize="small" aria-hidden />
            <Typography fontWeight={600}>Rising / Resolving pair integrity</Typography>
            {intData !== null && (
              <Chip
                size="small"
                color={
                  intData.missing_resolving.length + intData.missing_rising.length > 0
                    ? "warning"
                    : "success"
                }
                label={`${intData.missing_resolving.length} / ${intData.missing_rising.length}`}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {intLoading && (
            <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {intError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {intError}
            </Alert>
          )}
          {intData && (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary" display="block">
                Rule: resolving code = rising code + 10,000; same type, contract, and version. Rising
                &lt; 10,000; resolving in 10,000–19,999.
              </Typography>

              <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Missing resolving counterparts ({intData.missing_resolving.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {intData.missing_resolving.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None.
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        Paginated locally (full report from API).
                      </Typography>
                      <TableContainer sx={{ maxHeight: 280 }}>
                        <GapTable rows={missResSlice} onOpenEvent={setEditEventId} />
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={missResRows.length}
                        page={missResPage}
                        onPageChange={(_, p) => setMissResPage(p)}
                        rowsPerPage={missResRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        onRowsPerPageChange={(e) => {
                          setMissResRowsPerPage(parseInt(e.target.value, 10));
                          setMissResPage(0);
                        }}
                      />
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Missing rising counterparts ({intData.missing_rising.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {intData.missing_rising.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      None.
                    </Typography>
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        Paginated locally (full report from API).
                      </Typography>
                      <TableContainer sx={{ maxHeight: 280 }}>
                        <GapTable rows={missRisSlice} onOpenEvent={setEditEventId} />
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={missRisRows.length}
                        page={missRisPage}
                        onPageChange={(_, p) => setMissRisPage(p)}
                        rowsPerPage={missRisRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        onRowsPerPageChange={(e) => {
                          setMissRisRowsPerPage(parseInt(e.target.value, 10));
                          setMissRisPage(0);
                        }}
                      />
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <EventsFormDialog
        open={!!editEventId}
        onClose={() => {
          setEditEventId(null);
          refreshAfterDialog();
        }}
        onSaved={() => refreshAfterDialog()}
        eventId={editEventId}
        createNewEvent={false}
        onNavigateToEvent={(id) => setEditEventId(id)}
      />
    </Stack>
  );
};

export default AdminApprovalTools;
